import type {
  CustomsAgencyLiquidationConfig,
  LiquidationConfig,
} from '@/domain/types/configs/company/LiquidationConfig'

/**
 * Fallback genérico del módulo de Liquidación: el comportamiento para una
 * compañía nueva que todavía no ha configurado nada.
 *
 * Ningún valor de aquí puede salir de un cliente ya validado. La prueba de
 * diseño: si el default solo se sostiene porque "así opera el único cliente que
 * hemos visto", no es un default del producto — es un dato de validación, y el
 * campo debe quedar sin valor hasta que cada compañía lo configure.
 *
 * Convención de unidades: todo campo `*Pct` está en puntos porcentuales
 * (`0.5` = 0,5 %), y todo monto está en la moneda local de la compañía.
 */
/**
 * Con qué campos del embarque arranca la sección Embarque del Excel y del PDF.
 *
 * Son los cuatro que identifican el embarque en cualquier liquidación: la
 * referencia de Kila, el máster, la reserva y la fecha real de arribo. Quien
 * quiera otros los agrega en Configuración > Liquidación > Campos del embarque;
 * dejar la lista vacía ahí significa "todos los campos visibles del módulo".
 */
export const CAMPOS_EMBARQUE_POR_DEFECTO = ['ata', 'mbl', 'booking', 'idNumber']

/**
 * Con qué campos arranca la cabecera del documento. La referencia del embarque
 * sola es lo que traía antes de que la cabecera fuera configurable.
 */
export const CAMPOS_CABECERA_POR_DEFECTO = ['idNumber']

export const liquidationConfig: LiquidationConfig = {
  trm: {
    // Riesgo y Nacionalización son obligatorias para cualquier operación de importación.
    riesgoActivo: true,
    nacionalizacionPagoActivo: true,
    // Solo aplica si la compañía típicamente recibe facturas de flete de un
    // tercero local separado, en USD, con su propia tasa impresa.
    pagoNavieraActivo: false,
    pagoNavieraValidacionCruzadaActiva: true,
    pagoNavieraUmbralAlertaPct: 0.5,
    // Nunca bloqueo duro por defecto: cualquier compañía puede pagar a un tercero
    // por un mecanismo cambiario distinto al de la factura.
    pagoNavieraComportamiento: 'alerta_informativa',
    permiteAjusteManualBloqueAgenteCarga: true,
  },

  seguro: {
    // Exige factura real de aseguradora hasta que la compañía configure lo contrario.
    modoCalculo: 'no_aplica',
    cifUpliftPct: null,
    ratePct: [],
    fallbackSinConfigurar: 'bloquear_liquidacion_hasta_configurar',
  },

  // La fórmula de comisión depende del contrato de cada compañía con su agencia:
  // no hay default posible, se configura agencia por agencia.
  agenciasAduana: [],

  impuestos: {
    // Más simple de entender para una compañía sin convención propia todavía.
    gmfPoliticaClasificacion: 'centralizado_bloque_bancario',
    gmfTratamiento: 'incluir_siempre',
    // El IVA de un tercero logístico es, en general, descontable/acreditable.
    ivaTercerosTratamiento: 'excluir_siempre',
    ivaImportacionTratamiento: 'excluir_siempre',
  },

  depositosGarantia: {
    tratamiento: 'excluir_de_costos_con_registro_informativo',
    // Sin umbral: alertar siempre que se detecte un depósito por el texto del cargo.
    umbralAlertaMonto: null,
  },

  factorImportacion: {
    // Es la base más conservadora y la más fácil de auditar: excluye cualquier
    // cargo adicional facturado por el proveedor.
    base: 'valor_neto_mercancia_exw',
    // `prorrateo_sku` no está construido todavía.
    nivelCalculo: 'embarque_sabana_general',
    modeloProvisionVsRealActivo: false,
  },

  extraccion: {
    nomenclaturaPorModoActivo: false,
    exclusionesExtraccion: [],
    gobiernoOtrosUmbralAlertaPct: 5,
    tratamientoDescuentosProveedor: 'restar_del_valor_mercancia',
  },
  documento: {
    // Sin campo elegido el documento se nombra por la referencia de Kila, como siempre.
    campoNombre: null,
    camposEmbarque: [...CAMPOS_EMBARQUE_POR_DEFECTO],
    camposCabecera: [...CAMPOS_CABECERA_POR_DEFECTO],
    // Encendidas: apagarlas de entrada le quitaría a una compañía un aviso que
    // hoy recibe sin haber pedido el cambio.
    mostrarAlertas: true,
    // Sin carpeta: el PDF generado queda en la raíz de Archivos del embarque
    // hasta que la compañía escoja una de sus carpetas.
    carpetaDestinoId: null,
    // Una pre-liquidación es un paso del proceso de cada compañía, no del
    // producto: quien la use la enciende.
    preLiquidacionActiva: false,
    compararPreLiquidacion: false,
    // El formato con el que el PDF salía antes de ser configurable: horizontal,
    // las páginas que hagan falta y los decimales de la configuración regional.
    orientacion: 'horizontal',
    paginasObjetivo: null,
    decimales: null,
    // 2 decimales en moneda local, como antes de que fuera configurable.
    decimalesMonedaLocal: null,
  },
}

/** Agencia de aduana recién agregada: sin fórmula ni tarifas, bloquea hasta configurarse. */
export const createCustomsAgencyLiquidationConfig = (
  id: string,
  nombre: string
): CustomsAgencyLiquidationConfig => ({
  id,
  nombre,
  comisionFormula: null,
  comisionAdValoremPct: null,
  comisionPisoFijo: [],
  comisionFallbackSinConfigurar: 'bloquear_liquidacion_hasta_configurar',
  cargosFijos: [],
  unidadConteoDefault: 'dim_y_dav_igual',
})
